// game/client/src/day-night-clock.ts
//
// Day/night clock cue — top-centre HUD widget mirroring the server cycle state.
// Distilled from docs/design/mockups/hud-v10-daynight.html (.v5-phase-clock):
// an in-world time readout, a phase line (icon + "Day N"/"Night N" + countdown
// to the next transition), and a phase-progress bar — all themed by a per-phase
// accent colour that shifts gold (day) → orange (dusk) → blue (night) → tan (dawn).
//
// Server-authoritative: this only formats `room.state.{dayNightPhase, cycleElapsed,
// dayCount}`. Styles are injected once so the widget is self-contained (no
// dependency on style.css), matching the per-module HUD pattern (wonder-hud.ts).

import { phaseInfo, URGENT_SEC } from "./day-night-cycle";

const STYLE_ID = "dn-clock-style";
const CSS = `
.dn-clock {
  position: fixed; top: 14px; left: 50%; transform: translateX(-50%);
  z-index: 40; pointer-events: none;
  min-width: 188px; padding: 7px 18px 9px; text-align: center;
  background: rgba(12, 16, 22, 0.82); backdrop-filter: blur(4px);
  border: 1px solid var(--dn-color, #f7c360);
  border-radius: 6px;
  box-shadow: 0 0 14px color-mix(in srgb, var(--dn-color, #f7c360) 32%, transparent);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  transition: border-color 0.5s, box-shadow 0.5s;
}
.dn-clock.day  { --dn-color: #f7c360; }
.dn-clock.dusk { --dn-color: #e8703c; }
.dn-clock.night { --dn-color: #6a8ad8; }
.dn-clock.dawn { --dn-color: #d8c4a8; }
.dn-clock .dn-time {
  font-size: 19px; font-weight: 700; color: #ede4cf;
  letter-spacing: 0.08em; font-variant-numeric: tabular-nums; line-height: 1.1;
}
.dn-clock .dn-phase-line {
  display: flex; align-items: center; justify-content: center; gap: 5px;
  margin-top: 2px; font-size: 12px; font-weight: 600; letter-spacing: 0.03em;
}
.dn-clock .dn-icon { font-size: 13px; color: var(--dn-color, #f7c360); }
.dn-clock .dn-label { color: var(--dn-color, #f7c360); font-weight: 700; }
.dn-clock .dn-countdown { color: #8a8578; font-weight: 500; }
.dn-clock .dn-countdown.urgent { color: #e8703c; animation: dn-blink 1s infinite; }
@keyframes dn-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
.dn-clock .dn-bar {
  position: relative; height: 4px; margin-top: 6px; overflow: hidden;
  background: rgba(0, 0, 0, 0.55); border: 1px solid rgba(0, 0, 0, 0.85); border-radius: 2px;
}
.dn-clock .dn-bar-fill {
  height: 100%; width: 0;
  background: var(--dn-color, #f7c360);
  box-shadow: 0 0 6px var(--dn-color, #f7c360);
  transition: width 0.4s linear, background 0.5s;
}
`;

export interface DayNightClock {
  update(args: { phase: number; cycleElapsed: number; dayCount: number }): void;
}

function fmtCountdown(secs: number): string {
  const s = Math.max(0, Math.round(secs));
  const m = Math.floor(s / 60);
  const r = (s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}

export function createDayNightClock(): DayNightClock {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const root = document.createElement("div");
  root.className = "dn-clock day";
  root.innerHTML =
    `<div class="dn-time"></div>` +
    `<div class="dn-phase-line">` +
      `<span class="dn-icon"></span>` +
      `<span class="dn-label"></span>` +
      `<span class="dn-countdown"></span>` +
    `</div>` +
    `<div class="dn-bar"><div class="dn-bar-fill"></div></div>`;
  document.body.appendChild(root);

  const timeEl = root.querySelector(".dn-time") as HTMLElement;
  const iconEl = root.querySelector(".dn-icon") as HTMLElement;
  const labelEl = root.querySelector(".dn-label") as HTMLElement;
  const countdownEl = root.querySelector(".dn-countdown") as HTMLElement;
  const barFill = root.querySelector(".dn-bar-fill") as HTMLElement;

  let lastKey = "";

  function update({ phase, cycleElapsed, dayCount }: { phase: number; cycleElapsed: number; dayCount: number }): void {
    const info = phaseInfo(phase, cycleElapsed, dayCount);

    if (info.key !== lastKey) {
      root.className = `dn-clock ${info.key}`;
      lastKey = info.key;
    }
    timeEl.textContent = info.clock;
    iconEl.textContent = info.icon;
    labelEl.textContent = info.label;
    countdownEl.textContent = `· ${info.nextLabel} ${fmtCountdown(info.secsToTransition)}`;
    countdownEl.classList.toggle("urgent", info.secsToTransition <= URGENT_SEC);
    barFill.style.width = `${Math.round(info.phaseFraction * 100)}%`;
  }

  // Seed an initial "Day 1" render so the panel never flashes empty in the
  // window between module load and the first frame where room+world are ready.
  update({ phase: 0, cycleElapsed: 0, dayCount: 1 });

  return { update };
}
